import { json } from 'admin/src/fetchers/index';
import { fetchApi } from 'shared/src/fetchers';
import type { LmsCourseContent } from 'shared/src/types/lms';
import type { LmsEnrollmentStatus, LmsGradingType, LmsQuizStatus } from 'shared/src/utils/lmsStatus';
import type { TrainingClassStatus, UnavailableReasonCode } from 'shared/src/utils/trainingStatus';

export {
  LmsAssignmentType,
  LmsEnrollmentStatus,
  LmsGradingType,
  LmsQuizStatus,
} from 'shared/src/utils/lmsStatus';

export type SessionStatus = 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled';
export const AttendeeState = {
  Registered: 0,
  Attended: 1,
  Voided: 2,
} as const;

export type AttendeeState = (typeof AttendeeState)[keyof typeof AttendeeState];

export interface CourseRoleSummary {
  roleId: string;
  roleName: string;
}

export interface SessionListItem {
  id: string;
  locationName: string;
  status: SessionStatus;
  timeBlock?: SessionTimeBlock;
  attendeeCount: number;
  capacity: number;
  election?: SessionElection;
  isLms?: boolean;
}

export type LmsClassSessionCourseListItem = {
  createdAt: string;
  deleted: boolean;
  id: string;
  keyCustomerId: string;
  keyPollworkerClassSessionId: string;
  lhCourseUuid: string;
  updatedAt: string;
  version: string;
};

export interface CourseListItem {
  id: string;
  className: string;
  notes?: string;
  contactName?: string;
  prerequisiteId?: string;
  certificateOfCompletionTemplateId?: string;
  roles: CourseRoleSummary[];
  sessions: SessionListItem[];
  sessionCount: number;
  totalCapacity: number;
  totalAttendees: number;
  electionNames: string[];
}

export interface CourseRoleAssociation {
  roleTrainingId: string;
  roleId: string;
  roleName: string;
  canRemove: boolean;
  blockedReason?: string;
  activeRequirementCount: number;
}

export interface SessionTimeBlock {
  id: string;
  startDateTime: string;
  endDateTime: string;
  instructorName?: string;
}

export interface SessionElection {
  id: string;
  electionName: string;
}

export interface SessionAddress {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  locationContactName?: string;
}

export interface LmsSessionCompletionStats {
  notEnrolled: number;
  inProgress: number;
  completed: number;
  enrolledTotal: number;
}

export interface LmsQuizSummary {
  assignmentUuid: string;
  activityUuid: string;
  title: string;
  status: LmsQuizStatus;
  grade: number | null;
  maxGrade: number | null;
  gradingType: LmsGradingType;
  passThresholdPercentage: number | null;
  attemptCount: number;
  allowRetries: boolean;
  maxRetries: number | null;
  canRetry?: boolean;
  overallFeedback: string | null;
  passed: boolean | null;
}

export interface LmsSessionWorkerCompletion {
  evUserId: string;
  enrollmentStatus: LmsEnrollmentStatus;
  completedActivityCount: number;
  totalActivityCount: number;
  requirementIds: string[];
  quizzes?: LmsQuizSummary[];
}

export interface LmsSessionOverlay {
  sessionId?: string;
  lhCourseUuid?: string;
  courseName?: string;
  enrollmentStatus?: LmsEnrollmentStatus;
  completedActivityCount?: number;
  totalActivityCount?: number;
  requirementIds?: string[];
  stats?: LmsSessionCompletionStats;
}

export interface LmsSessionCompletion {
  sessionId: string;
  lhCourseUuid?: string | null;
  courseName?: string | null;
  stats: LmsSessionCompletionStats;
  workers: LmsSessionWorkerCompletion[];
}

export interface LmsAssociatedCourse {
  id: string;
  lhCourseUuid: string;
  name?: string | null;
  createdAt: string;
}

export interface SessionSummary {
  id: string;
  courseName?: string | null;
  locationName: string;
  teacherName?: string;
  capacity: number;
  attendeeCount: number;
  notes?: string;
  status: SessionStatus;
  election?: SessionElection;
  timeBlock?: SessionTimeBlock;
  address?: SessionAddress;
  canDelete: boolean;
  cannotDeleteReason?: string;
  canScan: boolean;
  isOutsideInProgress: boolean;
  attendeePayAmount?: number;
  lms?: LmsSessionOverlay;
}

export interface CourseDetail {
  id: string;
  className: string;
  notes?: string;
  passValue?: string;
  prerequisiteId?: string;
  prerequisiteClassName?: string;
  contactId?: string;
  contactName?: string;
  certificateOfCompletionTemplateId?: string;
  roles: CourseRoleAssociation[];
  readiness?: TrainingReadiness[];
}

export interface AttendeeResponse {
  id: string;
  evUserId: string;
  firstName: string;
  lastName: string;
  cellPhone?: string;
  voterRegistrationNumber?: string;
  emailAddress?: string;
  state: AttendeeState;
  voidReason?: number;
  registrationTime: string;
  dateMarkedAsAttended?: string;
  markedAsAttendedByName?: string;
}

export interface BulkItemResult {
  id: string;
  success: boolean;
  skipReason?: string;
}

export interface BulkOperationResult {
  totalRequested: number;
  succeeded: number;
  skipped: number;
  items: BulkItemResult[];
}

export interface ScanResult {
  found: boolean;
  added: boolean;
  checkedIn: boolean;
  message?: string;
  attendee?: AttendeeResponse;
}

export interface TrainingReadiness {
  courseId: string;
  courseName: string;
  electionId: string;
  electionName: string;
  workerUsersNeeding: ReadinessEvUser[];
  workerUsersCompleted: ReadinessEvUser[];
  workerUsersRegistered: ReadinessEvUser[];
  workerUsersUnblocked: ReadinessEvUser[];
  workerUsersBlocked: ReadinessEvUser[];
  blockReason?: number;
}

export interface ReadinessEvUser {
  id: string;
  firstName: string;
  lastName: string;
  status: number;
  email: string | null;
}

export interface AddRoleResponse {
  association: CourseRoleAssociation;
  infoMessage?: string;
  futureWorkersAffected: number;
  workersAlreadySatisfied: number;
}

export interface BlockedWorker {
  evUserId: string;
  workerName: string;
  email: string | null;
  hasEmail: boolean;
  earliestWorkDate: string;
  requirementIds: string[];
  sourceSchedule: BlockedWorkerSourceSchedule | null;
  isSourceScheduleConfirmed: boolean | null;
}

export enum BlockedWorkerSchedulePublishStatus {
  Draft = 0,
  Released = 1,
}

export interface BlockedWorkerSourceSchedule {
  id: string;
  workDate: string;
  userConfirmationStatus: number;
  publishStatus: BlockedWorkerSchedulePublishStatus;
}

export interface SessionNotificationRequest {
  templateId?: string;
  skipEmail?: boolean;
  workerIds: string[];
}

export interface CreateSessionResponse extends SessionSummary {
  notificationResult?: {
    emailsSent: number;
    workersNeedingManualContact: number;
  };
}

export interface TrainingRequirementSummary {
  id: string;
  description: string;
}

export interface WorkerTrainingClass {
  electionId: string;
  electionName: string;
  id: string;
  className: string;
  contactName?: string;
  phone?: string;
  status: TrainingClassStatus;
  requiredForRoles: string[];
  sessionId?: string;
  sessionDate?: string;
  sessionLocation?: string;
  sessionAddress?: string;
  completedDate?: string;
  availableSessionCount: number;
  availableSessions: {
    id?: string;
    location?: string;
    classSize: number;
    seatsAvailable: number;
    firstStart: string;
    finalEnd: string;
    isAfterWorkSchedule: boolean;
    firstWorkSchedule?: string;
  }[];
  requirementId?: string[];
  requirements: TrainingRequirementSummary[];
  isVoided: boolean;
  voidReason?: number;
  isExtraTraining: boolean;
  unavailableReason?: UnavailableReasonCode;
  attendanceNotRecorded: boolean;
  isLegacy: boolean;
  sessionRegistrationDate?: string;
  sessionAttendeeId?: string;
  attended: boolean;
}

export async function getCourses(params?: {
  electionIds?: string[];
  roleIds?: string[];
}): Promise<CourseListItem[]> {
  const query = new URLSearchParams();
  if (params?.electionIds?.length) query.set('electionIds', params.electionIds.join(','));
  if (params?.roleIds?.length) query.set('roleIds', params.roleIds.join(','));

  const qs = query.toString();
  return json(await fetchApi(`/api/v3/pollworker/courses${qs ? `?${qs}` : ''}`, { method: 'GET' }));
}

export async function getPollworkerTrainingOverview(evUserId: string, electionIds?: string[]): Promise<WorkerTrainingClass[]> {
  const query = new URLSearchParams();
  if (electionIds?.length) query.set('electionIds', electionIds.join(','));

  const qs = query.toString();
  return json(
    await fetchApi(`/api/v3/pollworker/courses/workers/${evUserId}/training-overview${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    }),
  );
}

export async function getCourse(courseId: string, electionIds?: string[]): Promise<CourseDetail> {
  const query = new URLSearchParams();
  if (electionIds?.length) query.set('electionIds', electionIds.join(','));

  const qs = query.toString();
  return json(await fetchApi(`/api/v3/pollworker/courses/${courseId}${qs ? `?${qs}` : ''}`, { method: 'GET' }));
}

export async function createCourse(data: {
  className: string;
  keyEVUserContactId?: string;
  notes?: string;
  passValue?: string;
  prerequisiteId?: string;
  certificateOfCompletionTemplateId?: string;
}): Promise<CourseDetail> {
  return json(await fetchApi('/api/v3/pollworker/courses', { method: 'POST', body: JSON.stringify(data) }));
}

export async function getReadiness(): Promise<TrainingReadiness[]> {
  return json(await fetchApi('/api/v3/pollworker/courses/readiness', { method: 'GET' }));
}

export async function getBlockedWorkers(courseId: string, electionId: string): Promise<BlockedWorker[]> {
  return json(await fetchApi(`/api/v3/pollworker/courses/${courseId}/readiness/${electionId}/workers`, { method: 'GET' }));
}

export async function getSessions(courseId: string, electionIds?: string[]): Promise<SessionSummary[]> {
  const query = new URLSearchParams();
  if (electionIds?.length) query.set('electionIds', electionIds.join(','));

  const qs = query.toString();
  return json(await fetchApi(`/api/v3/pollworker/courses/${courseId}/sessions${qs ? `?${qs}` : ''}`, { method: 'GET' }));
}

export async function createSession(
  courseId: string,
  data: {
    locationName: string;
    capacity: number;
    teacherName?: string;
    electionId?: string;
    notes?: string;
    attendeePayAmount?: number;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    locationContactName?: string;
    timeBlock?: {
      startDateTime: string;
      endDateTime: string;
      instructorName?: string;
      notes?: string;
    };
    notification?: SessionNotificationRequest;
  },
): Promise<CreateSessionResponse> {
  return json(await fetchApi(`/api/v3/pollworker/courses/${courseId}/sessions`, { method: 'POST', body: JSON.stringify(data) }));
}

export async function updateSession(
  sessionId: string,
  data: Partial<{
    locationName: string;
    capacity: number;
    teacherName: string;
    electionId: string;
    notes: string;
    attendeePayAmount: number;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    locationContactName: string;
    timeBlock: Partial<{
      startDateTime: string;
      endDateTime: string;
      instructorName: string;
      notes: string;
    }>;
  }>,
): Promise<SessionSummary> {
  return json(await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}`, { method: 'PATCH', body: JSON.stringify(data) }));
}

export async function getSession(sessionId: string): Promise<SessionSummary> {
  return json(await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}`, { method: 'GET' }));
}

export async function getAssociatedLmsCourses(): Promise<LmsAssociatedCourse[]> {
  return json(await fetchApi('/api/v3/pollworker/courses/lms/associated', { method: 'GET' }));
}

export async function getLmsCourseContent(courseUuid: string): Promise<LmsCourseContent> {
  return json(await fetchApi(`/api/v3/pollworker/courses/lms/${courseUuid}`, { method: 'GET' }));
}

export const getLmsActivityPdf = async (courseUuid: string, activityUuid: string): Promise<Blob> => {
  const response = await fetchApi(`/api/v3/pollworker/courses/lms/${encodeURIComponent(courseUuid)}/activities/${encodeURIComponent(activityUuid)}/pdf`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Failed to fetch LearnHouse PDF');
  }
  return await response.blob();
};

function blockMediaQuery(blockUuid?: string, fileFormat?: string) {
  const params = new URLSearchParams();
  if (blockUuid) {
    params.set('blockUuid', blockUuid);
  }
  if (fileFormat) {
    params.set('fileFormat', fileFormat);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export const getLmsBlockImage = async (
  courseUuid: string,
  activityUuid: string,
  request: {
    fileId: string;
    blockUuid?: string;
    fileFormat?: string;
  },
): Promise<Blob> => {
  const response = await fetchApi(
    `/api/v3/pollworker/courses/lms/${encodeURIComponent(courseUuid)}/activities/${encodeURIComponent(activityUuid)}/blocks/${encodeURIComponent(request.fileId)}/image${blockMediaQuery(request.blockUuid, request.fileFormat)}`,
    {
      method: 'GET',
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch LearnHouse block image');
  }
  return await response.blob();
};

export const getLmsBlockPdf = async (
  courseUuid: string,
  activityUuid: string,
  request: {
    fileId: string;
    blockUuid?: string;
    fileFormat?: string;
  },
): Promise<Blob> => {
  const response = await fetchApi(
    `/api/v3/pollworker/courses/lms/${encodeURIComponent(courseUuid)}/activities/${encodeURIComponent(activityUuid)}/blocks/${encodeURIComponent(request.fileId)}/pdf${blockMediaQuery(request.blockUuid, request.fileFormat)}`,
    {
      method: 'GET',
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch LearnHouse block PDF');
  }
  return await response.blob();
};

export async function getSessionLmsCompletion(sessionId: string, init?: RequestInit): Promise<LmsSessionCompletion> {
  return json(await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/lms/completion`, { method: 'GET', ...init }));
}

export async function bindSessionLmsCourse(sessionId: string, lhCourseUuid: string): Promise<void> {
  const resp = await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/lms/course`, {
    method: 'PUT',
    body: JSON.stringify({ lhCourseUuid }),
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || body.detail || 'Failed to bind remote learning course');
  }
}

export async function unbindSessionLmsCourse(sessionId: string): Promise<void> {
  const resp = await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/lms/course`, { method: 'DELETE' });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || body.detail || 'Failed to unbind remote learning course');
  }
}

export type ResetSessionLmsProgressResult = {
  reset: number;
  quizCleanupFailedEvUserIds: string[];
};

export async function resetSessionLmsProgress(sessionId: string, evUserIds: string[]): Promise<ResetSessionLmsProgressResult> {
  const resp = await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/lms/progress/reset`, {
    method: 'POST',
    body: JSON.stringify({ evUserIds }),
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || body.detail || 'Failed to reset progress');
  }

  const body = await json(resp);
  return {
    reset: body.reset ?? 0,
    quizCleanupFailedEvUserIds: Array.isArray(body.quizCleanupFailedEvUserIds) ? body.quizCleanupFailedEvUserIds : [],
  };
}

export async function deleteSession(sessionId: string): Promise<void> {
  const resp = await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}`, { method: 'DELETE' });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to delete session');
  }
}

export async function getAttendees(sessionId: string): Promise<AttendeeResponse[]> {
  return json(await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/attendees`, { method: 'GET' }));
}

export interface EligibleAttendee {
  evUserId: string;
  firstName: string;
  lastName: string;
  cellPhone?: string;
  voterRegistrationNumber?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface EligibleAttendeesResult {
  hasElection: boolean;
  electionId?: string;
  electionName?: string;
  workers: EligibleAttendee[];
}

export async function getEligibleAttendees(sessionId: string): Promise<EligibleAttendeesResult> {
  return json(await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/eligible-attendees`, { method: 'GET' }));
}

export async function addAttendees(sessionId: string, evUserIds: string[]): Promise<BulkOperationResult> {
  return json(
    await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/attendees`, {
      method: 'POST',
      body: JSON.stringify({ evUserIds }),
    }),
  );
}

export async function removeAttendees(sessionId: string, attendeeIds: string[]): Promise<BulkOperationResult> {
  return json(
    await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/attendees/remove`, {
      method: 'POST',
      body: JSON.stringify({ attendeeIds }),
    }),
  );
}

export interface ReassignAttendeeResponse {
  success: boolean;
  message: string | null;
  emailSent: boolean;
}

export async function reassignAttendee(fromSessionId: string, toSessionId: string, evUserId: string): Promise<ReassignAttendeeResponse> {
  return json(
    await fetchApi('/api/v3/pollworker/courses/sessions/reassign', {
      method: 'POST',
      body: JSON.stringify({ fromSessionId, toSessionId, evUserId }),
    }),
  );
}

export async function markAttended(sessionId: string, attendeeIds: string[]): Promise<BulkOperationResult> {
  return json(
    await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/attendees/attend`, {
      method: 'POST',
      body: JSON.stringify({ attendeeIds }),
    }),
  );
}

export async function unmarkAttended(sessionId: string, attendeeIds: string[]): Promise<BulkOperationResult> {
  return json(
    await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/attendees/unattend`, {
      method: 'POST',
      body: JSON.stringify({ attendeeIds }),
    }),
  );
}

export async function scanCheckIn(sessionId: string, registrationNumber: string): Promise<ScanResult> {
  return json(
    await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/attendees/scan`, {
      method: 'POST',
      body: JSON.stringify({ registrationNumber }),
    }),
  );
}

export async function getSessionAttendeesReport(sessionId: string): Promise<void> {
  const resp = await fetchApi(`/api/v3/pollworker/courses/sessions/${sessionId}/attendees/report`, { method: 'GET' });

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to download report');
  }

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = 'AttendanceList.pdf';
  document.body.appendChild(a);
  a.click();

  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function getInstructors(): Promise<string[]> {
  return json(await fetchApi('/api/v3/pollworker/instructors', { method: 'GET' }));
}

export type RoleChangePolicy = 'ApplyToAll' | 'NewSchedulesOnly' | 'VoidExisting' | 'KeepExisting';

export interface WorkerImpactSummary {
  evUserId: string;
  scheduleId: string;
  workerName: string;
  email?: string;
  hasEmail: boolean;
  roleName: string;
  existingRequirementId?: string;
  requirementSatisfied?: boolean;
  requirementVoided?: boolean;
  confirmationStatus: number;
  isPendingConfirmation: boolean;
}

export interface ElectionImpact {
  electionId: string;
  electionName: string;
  workersLosingRequirement: number;
  workersGainingRequirement: number;
  workersUnchanged: number;
  losingWorkers: WorkerImpactSummary[];
  gainingWorkers: WorkerImpactSummary[];
  unchangedWorkers: WorkerImpactSummary[];
  gainingScheduleIds: string[];
  losingScheduleIds: string[];
}

export interface RoleChangePreviewRequest {
  roleIdsToAdd: string[];
  roleIdsToRemove: string[];
}

export interface RoleChangePreviewResponse {
  elections: ElectionImpact[];
}

export interface ElectionDecision {
  electionId: string;
  additionPolicy: RoleChangePolicy;
  removalPolicy: RoleChangePolicy;
  expectedGainingScheduleIds?: string[];
  expectedLosingScheduleIds?: string[];
}

export interface RoleChangeNotificationOptions {
  templateId?: string;
  skipEmail: boolean;
}

export interface ApplyRoleChangesRequest {
  roleIdsToAdd: string[];
  roleIdsToRemove: string[];
  electionDecisions: ElectionDecision[];
  notification?: RoleChangeNotificationOptions;
}

export interface RoleChangeResult {
  roleId: string;
  roleName: string;
  changeType: string;
  success: boolean;
  error?: string;
}

export interface ElectionDriftDetail {
  electionId: string;
  electionName: string;
  expectedGaining: number;
  currentGaining: number;
  gainingAdded: number;
  gainingRemoved: number;
  expectedLosing: number;
  currentLosing: number;
  losingAdded: number;
  losingRemoved: number;
}

export interface ApplyRoleChangesResponse {
  success: boolean;
  requirementsCreated: number;
  requirementsVoided: number;
  emailsQueued: number;
  roleChanges: RoleChangeResult[];
  driftDetected: boolean;
  errorMessage?: string;
  driftDetails?: ElectionDriftDetail[];
}

export async function previewRoleChanges(courseId: string, request: RoleChangePreviewRequest): Promise<RoleChangePreviewResponse> {
  return json(
    await fetchApi(`/api/v3/pollworker/courses/${courseId}/role-changes/preview`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),
  );
}

export async function applyRoleChanges(courseId: string, request: ApplyRoleChangesRequest): Promise<ApplyRoleChangesResponse> {
  return json(
    await fetchApi(`/api/v3/pollworker/courses/${courseId}/role-changes/apply`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),
  );
}

export async function updateCourse(
  courseId: string,
  data: {
    className?: string;
    keyEVUserContactId?: string;
    notes?: string;
    passValue?: string;
    prerequisiteId?: string;
    certificateOfCompletionTemplateId?: string;
  },
): Promise<CourseDetail> {
  return json(
    await fetchApi(`/api/v3/pollworker/courses/${courseId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  );
}

export async function deleteCourse(courseId: string): Promise<void> {
  const resp = await fetchApi(`/api/v3/pollworker/courses/${courseId}`, { method: 'DELETE' });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to delete course');
  }
}

export async function getRolesForCourse(courseId: string): Promise<CourseRoleAssociation[]> {
  return json(await fetchApi(`/api/v3/pollworker/courses/${courseId}/roles`, { method: 'GET' }));
}

export async function rescheduleCourse(sessionId: string, scheduleId: string, payload: PollworkerRescheduleSessionRequest): Promise<PollworkerRescheduleSessionResponse> {
  return json(
    await fetchApi(`/api/v3/pw/trainings/${sessionId}/reschedule/${scheduleId}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  );
}

export interface PollworkerRescheduleSessionRequest {
  newStartTime: string;
  newEndTime: string;

  keepExistingAttendees: boolean;
}

export interface PollworkerRescheduleSessionResponse {
  sessionId: string;
  scheduleId: string;
  newStartTime: string;
  newEndTime: string;
  emailsSent: number;
  emailsSkippedPastSession: boolean;
}

export type WorkerRequirementStatus = 'ScheduledFuture' | 'ScheduledPastUnattended' | 'Unscheduled';

export interface RequirementsOverviewResponse {
  elections: ElectionRequirementsGroup[];
  items: WorkerRequirementItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ElectionRequirementsGroup {
  electionId: string;
  electionName: string;
  electionDate: string;
  courses: CourseRequirementsGroup[];
}

export interface CourseRequirementsGroup {
  courseId: string;
  courseName: string;
  stats: RequirementsStats;
  workers: WorkerRequirementItem[];
}

export interface RequirementsStats {
  scheduledFuture: number;
  scheduledPastUnattended: number;
  unscheduled: number;
  total: number;
}

export interface WorkerRequirementItem {
  evUserId: string;
  workerName: string;
  email: string | null;
  status: WorkerRequirementStatus;
  attendeeId: string | null;
  sessionId: string | null;
  sessionName: string | null;
  sessionDate: string | null;
  isLms: boolean;
  requirementId: string | null;
  scheduleId: string | null;
  workDate: string | null;
  roleName: string | null;
  courseName: string | null;
  courseId: string | null;
  electionName: string | null;
  electionId: string | null;
  confirmationStatus: number | null;
  isVoided: boolean;
  voidReason: number | null;
  voidReasonNotes: string | null;
  voidedAt: string | null;
  voidedByName: string | null;
}

export interface FutureSessionItem {
  sessionId: string;
  locationName: string;
  startDateTime: string;
  endDateTime: string;
  capacity: number;
  attendeeCount: number;
  seatsAvailable: number;
}

export async function getRequirementsOverview(params?: {
  electionIds?: string[];
  courseIds?: string[];
  workerIds?: string[];
  workerName?: string;
  status?: string;
  includeCompleted?: boolean;
  page?: number;
  pageSize?: number;
  groupBy?: string;
  includeVoided?: boolean;
  confirmationStatus?: number | null;
}): Promise<RequirementsOverviewResponse> {
  const query = new URLSearchParams();
  if (params?.electionIds?.length) query.set('electionIds', params.electionIds.join(','));
  if (params?.courseIds?.length) query.set('courseIds', params.courseIds.join(','));
  if (params?.workerIds?.length) query.set('workerIds', params.workerIds.join(','));
  if (params?.workerName) query.set('workerName', params.workerName);
  if (params?.status) query.set('status', params.status);
  if (params?.includeCompleted) query.set('includeCompleted', 'true');
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  if (params?.groupBy) query.set('groupBy', params.groupBy);
  if (params?.includeVoided) query.set('includeVoided', 'true');
  if (params?.confirmationStatus !== undefined && params?.confirmationStatus !== null) {
    query.set('confirmationStatus', params.confirmationStatus.toString());
  }

  const qs = query.toString();
  return json(await fetchApi(`/api/v3/pollworker/courses/requirements/overview${qs ? `?${qs}` : ''}`, { method: 'GET' }));
}

export async function getFutureSessions(courseId: string, electionId?: string): Promise<FutureSessionItem[]> {
  const query = new URLSearchParams();
  if (electionId) query.set('electionId', electionId);
  const qs = query.toString();
  return json(await fetchApi(`/api/v3/pollworker/courses/${courseId}/future-sessions${qs ? `?${qs}` : ''}`, { method: 'GET' }));
}

export interface WorkerOption {
  id: string;
  name: string;
}

export interface FilterOptionsResponse {
  workers: WorkerOption[];
  totalWorkerCount: number;
}

export async function getFilterOptions(params?: {
  search?: string;
  limit?: number;
}): Promise<FilterOptionsResponse> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());

  const qs = query.toString();
  return json(await fetchApi(`/api/v3/pollworker/courses/requirements/filter-options${qs ? `?${qs}` : ''}`, { method: 'GET' }));
}
